// tripTimeline — the words for one entry of the Lodge trip dashboard's timeline
// (GET /outfitter/dashboard/trips → timeline[]), shared by the web "Current
// trip" tab (frontend TripDashboard.jsx) and the iOS Today tab, so the two
// cannot describe the same day differently. The API builds the entries in
// api/src/outfitterTripDashboard.js.
//
// Every kind the API sends has its own branch; an unknown kind reads as a
// plain "Scheduled", never as some other kind (iOS once showed a room turnover
// as "Massage" because its last branch caught everything).

export interface TimelineLeg {
  direction?: string | null; transportType?: string | null; place?: string | null;
  airline?: string | null; flightNo?: string | null; tailNo?: string | null; people?: number | null; when?: string | null;
}
export interface TimelineEntry {
  kind: string;
  time?: string | null;
  group?: string;
  leg?: TimelineLeg;
  meal?: { meal: string; entrees?: string[]; headcount?: number | null };
  activity?: { label: string };
  massage?: { count: number; minutes?: number | null };
  turnover?: { rooms: string[]; text?: string | null };
}

export const MEAL_LABELS: Record<string, string> = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", appetizer: "Appetizers", other: "Meal" };

/** "6:30 AM" from "06:30"; "" when it is not a clock. */
export function clock12(hhmm?: string | null): string {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm || ""));
  if (!m) return "";
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
}

/** How a leg travels, in words. */
export function legHow(l?: TimelineLeg | null): string {
  if (!l) return "";
  if (l.transportType === "ground") return l.place ? `Driving ${l.direction === "departure" ? "to" : "from"} ${l.place}` : "Driving";
  if (l.transportType === "commercial_air") return ["Commercial air", [l.airline, l.flightNo].filter(Boolean).join(" ")].filter(Boolean).join(" · ");
  if (l.transportType === "private_air") return ["Private air", l.tailNo ? `Tail ${l.tailNo}` : ""].filter(Boolean).join(" · ");
  return "How they travel is not recorded";
}

export type TimelineTag = "IN" | "OUT" | "ROOMS" | null;

/** One entry in words: a tag (IN / OUT / ROOMS), what happens, a detail line, and the time column. */
export function timelineEntryText(it: TimelineEntry): { tag: TimelineTag; what: string; meta: string | null; timeLabel: string } {
  let tag: TimelineTag = null; let what = "Scheduled"; let meta: string | null = null;
  if (it.kind === "arrival" || it.kind === "departure") {
    tag = it.kind === "arrival" ? "IN" : "OUT";
    const n = it.leg?.people;
    what = `${it.kind === "arrival" ? "Arriving" : "Departing"}${n ? ` · ${n} ${n === 1 ? "person" : "people"}` : ""}`;
    meta = legHow(it.leg) || null;
  } else if (it.kind === "meal") {
    what = (it.meal && MEAL_LABELS[it.meal.meal]) || "Meal";
    meta = [(it.meal?.entrees || []).join(", "), it.meal?.headcount != null ? `${it.meal.headcount} plates` : null].filter(Boolean).join(" · ") || null;
  } else if (it.kind === "turnover") {
    tag = "ROOMS";
    what = `Room turnover · ${(it.turnover?.rooms || []).join(", ")}`;
    meta = it.turnover?.text || null;
  } else if (it.kind === "activity") {
    what = it.activity?.label || "Activity";
  } else if (it.kind === "massage") {
    const c = it.massage?.count || 1;
    what = `Massage${c > 1 ? `s × ${c}` : ""}`;
    meta = it.massage?.minutes ? `${it.massage.minutes} min each` : null;
  }
  const timeLabel = it.time ? clock12(it.time)
    : it.kind === "meal" ? what
      : it.kind === "massage" || it.kind === "activity" ? "Today" : "Time not set";
  return { tag, what, meta, timeLabel };
}
